import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEdit, FaChevronRight, FaArrowLeft, FaVideo, FaFilePdf } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';

const ManageCourses = () => {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grades'); // 'grades', 'months', 'materials'
  
  // Data States
  const [grades, setGrades] = useState([]);
  const [months, setMonths] = useState([]);
  const [materials, setMaterials] = useState([]);
  
  // Selection States
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('grade'); // 'grade', 'month', 'material'
  const [formData, setFormData] = useState({
    id: null,
    title: '', description: '', price: '', tag: '', bg_pattern: 'bg-gradient-to-br from-blue-600 to-indigo-800',
    teacher_name: '', schedule: '', subject: '', medium: 'Sinhala',
    month_name: '', material_type: 'video', material_url: '', pdf_file: null, scheduled_at: '', ended_at: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      if (view === 'grades') {
        const response = await fetch(`${API_BASE_URL}/admin/manage_courses.php`);
        const data = await response.json();
        if (data.status === 'success') setGrades(data.data);
      } else if (view === 'months') {
        const response = await fetch(`${API_BASE_URL}/admin/manage_months.php?course_id=${selectedGrade.id}`);
        const data = await response.json();
        if (data.status === 'success') setMonths(data.data);
      } else if (view === 'materials') {
        const response = await fetch(`${API_BASE_URL}/admin/manage_materials.php?month_id=${selectedMonth.id}`);
        const data = await response.json();
        if (data.status === 'success') setMaterials(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [view, selectedGrade, selectedMonth]);

  const handleEdit = (item, type) => {
    setModalType(type);
    if (type === 'grade') {
      setFormData({
        id: item.id,
        title: item.title,
        description: item.description,
        price: item.price,
        tag: item.tag,
        teacher_name: item.teacher_name,
        schedule: item.schedule,
        subject: item.subject,
        medium: item.medium || 'Sinhala',
        bg_pattern: item.bg_pattern
      });
    } else if (type === 'month') {
      setFormData({
        id: item.id,
        month_name: item.month_name,
        price: item.price
      });
    } else if (type === 'material') {
      setFormData({
        id: item.id,
        title: item.title,
        description: item.description || '',
        material_type: item.type,
        material_url: item.url,
        pdf_file: null,
        scheduled_at: item.scheduled_at || '',
        ended_at: item.ended_at || ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let url = '';
    let body = { id: formData.id };
    let fetchBody = null;
    let headers = { 'Content-Type': 'application/json' };

    if (modalType === 'grade') {
      url = `${API_BASE_URL}/admin/manage_courses.php`;
      body = { 
        ...body,
        title: formData.title, 
        description: formData.description, 
        price: formData.price, 
        tag: formData.tag, 
        teacher_name: formData.teacher_name,
        schedule: formData.schedule,
        subject: formData.subject,
        medium: formData.medium,
        bg_pattern: formData.bg_pattern 
      };
      fetchBody = JSON.stringify(body);
    } else if (modalType === 'month') {
      url = `${API_BASE_URL}/admin/manage_months.php`;
      body = { ...body, course_id: selectedGrade.id, month_name: formData.month_name, price: formData.price };
      fetchBody = JSON.stringify(body);
    } else if (modalType === 'material') {
      url = `${API_BASE_URL}/admin/manage_materials.php`;
      const formDataObj = new FormData();
      if (formData.id) formDataObj.append('id', formData.id);
      formDataObj.append('month_id', selectedMonth.id);
      formDataObj.append('title', formData.title);
      formDataObj.append('description', formData.description || '');
      formDataObj.append('type', formData.material_type);
      
      if (formData.material_type === 'pdf') {
        if (formData.pdf_file) formDataObj.append('pdf_file', formData.pdf_file);
        if (formData.material_url) formDataObj.append('url', formData.material_url); // keep old url if not replacing
      } else {
        formDataObj.append('url', formData.material_url || '');
      }
      
      if (formData.scheduled_at) formDataObj.append('scheduled_at', formData.scheduled_at);
      if (formData.ended_at) formDataObj.append('ended_at', formData.ended_at);
      
      fetchBody = formDataObj;
      headers = {}; // Let browser set multipart/form-data boundary
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        ...(Object.keys(headers).length > 0 && { headers }),
        body: fetchBody
      });
      const data = await response.json();
      if (data.status === 'success') {
        alert(data.message);
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        alert("Error: " + data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Submission failed. Check console for details.");
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    let url = '';
    if (type === 'grade') url = `${API_BASE_URL}/admin/manage_courses.php?id=${id}`;
    else if (type === 'month') url = `${API_BASE_URL}/admin/manage_months.php?id=${id}`;
    else if (type === 'material') url = `${API_BASE_URL}/admin/manage_materials.php?id=${id}`;

    try {
      const response = await fetch(url, { method: 'DELETE' });
      const data = await response.json();
      if (data.status === 'success') fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      title: '', description: '', price: '', tag: '', bg_pattern: 'bg-gradient-to-br from-blue-600 to-indigo-800',
      teacher_name: '', schedule: '', subject: '', medium: 'Sinhala',
      month_name: '', material_type: 'video', material_url: '', pdf_file: null, scheduled_at: '', ended_at: ''
    });
  };

  const handleBack = () => {
    if (view === 'materials') setView('months');
    else if (view === 'months') setView('grades');
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs & Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
          <span className={`cursor-pointer hover:text-orange-500 ${view === 'grades' ? 'text-orange-500' : ''}`} onClick={() => setView('grades')}>Grades</span>
          {selectedGrade && (
            <>
              <FaChevronRight size={10} />
              <span className={`cursor-pointer hover:text-orange-500 ${view === 'months' ? 'text-orange-500' : ''}`} onClick={() => setView('months')}>{selectedGrade.title}</span>
            </>
          )}
          {selectedMonth && view === 'materials' && (
            <>
              <FaChevronRight size={10} />
              <span className="text-orange-500">{selectedMonth.month_name}</span>
            </>
          )}
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            {view !== 'grades' && (
              <button onClick={handleBack} className="p-2 bg-white rounded-lg border border-slate-100 text-slate-400 hover:text-orange-500 shadow-sm transition-all">
                <FaArrowLeft />
              </button>
            )}
            <h2 className="text-xl font-bold text-slate-800">
              {view === 'grades' ? 'Manage Grades' : view === 'months' ? `Months for ${selectedGrade.title}` : `Materials for ${selectedMonth.month_name}`}
            </h2>
          </div>
          <button 
            onClick={() => { setModalType(view === 'grades' ? 'grade' : view === 'months' ? 'month' : 'material'); setShowModal(true); }}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg shadow-orange-200 transition-all active:scale-95"
          >
            <FaPlus /> Add {view === 'grades' ? 'Grade' : view === 'months' ? 'Month' : 'Material'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          {view === 'grades' && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                    <th className="px-8 py-5">Class Identity</th>
                    <th className="px-8 py-5">Assigned Teacher</th>
                    <th className="px-8 py-5">Schedule & Mode</th>
                    <th className="px-8 py-5">Premium Fee</th>
                    <th className="px-8 py-5 text-right">Operations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {grades.map((grade) => (
                    <tr key={grade.id} className="group hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => { setSelectedGrade(grade); setView('months'); }}>
                      <td className="px-8 py-6">
                        <div className="space-y-1">
                          <h4 className="font-bold text-slate-800 text-sm">{grade.title}</h4>
                          <div className="flex gap-2">
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px] font-black uppercase tracking-widest">{grade.tag}</span>
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-400 rounded text-[9px] font-black uppercase tracking-widest">{grade.subject}</span>
                            <span className="px-2 py-0.5 bg-orange-50 text-orange-600 rounded text-[9px] font-black uppercase tracking-widest">{grade.medium}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 font-bold text-xs border border-slate-100">
                            {grade.teacher_name?.charAt(0) || 'T'}
                          </div>
                          <div>
                            <p className="font-black text-slate-700 text-xs uppercase tracking-tight leading-tight">{grade.teacher_name}</p>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Teacher</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="space-y-1">
                          <p className="text-xs font-bold text-slate-600">{grade.schedule}</p>
                          <span className="flex items-center gap-1.5 text-[9px] font-black text-blue-500 uppercase tracking-widest">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span> Online
                          </span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className="text-sm font-black text-slate-800">LKR {grade.price}</span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex justify-end gap-3" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => handleEdit(grade, 'grade')} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"><FaEdit /></button>
                          <button onClick={() => handleDelete(grade.id, 'grade')} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><FaTrash /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === 'months' && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                    <th className="px-8 py-5">Month Reference</th>
                    <th className="px-8 py-5">Enrollment Status</th>
                    <th className="px-8 py-5 text-right">Operations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {months.map((month) => (
                    <tr key={month.id} className="group hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => { setSelectedMonth(month); setView('materials'); }}>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-xl flex items-center justify-center font-black text-sm uppercase">
                            {month.month_name.substring(0, 3)}
                          </div>
                          <div>
                            <h3 className="font-bold text-slate-800 text-sm">{month.month_name}</h3>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Academic Period</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className="flex items-center gap-2 text-[10px] font-black text-teal-600 uppercase tracking-widest bg-teal-50 px-3 py-1 rounded-full w-fit">
                          <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span> Active
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex justify-end gap-3" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => handleEdit(month, 'month')} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all">
                            <FaEdit />
                          </button>
                          <button onClick={() => handleDelete(month.id, 'month')} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                            <FaTrash />
                          </button>
                          <div className="p-2 text-slate-300 group-hover:text-orange-500 transition-all">
                            <FaChevronRight />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {months.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-8 py-12 text-center text-slate-400 font-medium italic">No months created yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {view === 'materials' && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                    <th className="px-8 py-5">Material Detail</th>
                    <th className="px-8 py-5">Type</th>
                    <th className="px-8 py-5">Access Link</th>
                    <th className="px-8 py-5 text-right">Operations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {materials.map((mat) => (
                    <tr key={mat.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${mat.type === 'video' ? 'bg-red-50 text-red-500' : mat.type === 'pdf' ? 'bg-blue-50 text-blue-500' : mat.type === 'live' ? 'bg-teal-50 text-teal-500' : 'bg-purple-50 text-purple-500'}`}>
                            {mat.type === 'video' || mat.type === 'recording' ? <FaVideo /> : mat.type === 'pdf' ? <FaFilePdf /> : <div className="w-2 h-2 bg-teal-500 rounded-full animate-ping"></div>}
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-800 text-sm">{mat.title}</h4>
                            {mat.type === 'live' && mat.scheduled_at && (
                              <p className="text-[10px] text-teal-500 font-black uppercase tracking-widest flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
                                {new Date(mat.scheduled_at).toLocaleDateString()} | 
                                {new Date(mat.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} 
                                {mat.ended_at && ` - ${new Date(mat.ended_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${mat.type === 'video' ? 'bg-red-100 text-red-600' : mat.type === 'pdf' ? 'bg-blue-100 text-blue-600' : mat.type === 'live' ? 'bg-teal-100 text-teal-600' : 'bg-purple-100 text-purple-600'}`}>
                          {mat.type}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <a href={mat.url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-blue-500 hover:underline truncate max-w-[200px] block">
                          {mat.url}
                        </a>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex justify-end gap-3">
                          <button onClick={() => handleEdit(mat, 'material')} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all">
                            <FaEdit />
                          </button>
                          <button onClick={() => handleDelete(mat.id, 'material')} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {materials.length === 0 && (
                    <tr>
                      <td colSpan="4" className="px-8 py-12 text-center text-slate-400 font-medium italic">No materials found for this month.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Unified Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl p-10 shadow-2xl overflow-y-auto max-h-[90vh]">
            <h3 className="text-2xl font-black text-slate-800 mb-8">{formData.id ? 'Edit' : 'Add New'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {modalType === 'grade' ? (
                <div className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <Input label="Class Identity (Title)" placeholder="e.g. Grade 10 - Theory Class" value={formData.title} onChange={(v) => setFormData({...formData, title: v})} />
                  </div>
                  <Input label="Grade Tag" placeholder="e.g. GRADE 10" value={formData.tag} onChange={(v) => setFormData({...formData, tag: v})} />
                  <Input label="Subject" placeholder="e.g. MATHEMATICS" value={formData.subject} onChange={(v) => setFormData({...formData, subject: v})} />
                  <Input label="Assigned Teacher" placeholder="e.g. MANOJ SAMPATH" value={formData.teacher_name} onChange={(v) => setFormData({...formData, teacher_name: v})} />
                  <div className="space-y-1">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Medium</label>
                    <select 
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:border-orange-500 font-bold text-sm shadow-inner"
                      value={formData.medium}
                      onChange={(e) => setFormData({...formData, medium: e.target.value})}
                    >
                      <option value="Sinhala">Sinhala</option>
                      <option value="English">English</option>
                    </select>
                  </div>
                  <Input label="Premium Fee (LKR)" value={formData.price} type="number" onChange={(v) => setFormData({...formData, price: v})} />
                  <div className="col-span-2">
                    <Input label="Schedule" placeholder="e.g. Monday 2.30 - 4.30 pm" value={formData.schedule} onChange={(v) => setFormData({...formData, schedule: v})} />
                  </div>
                  <div className="col-span-2">
                    <Input label="Description" value={formData.description} type="textarea" onChange={(v) => setFormData({...formData, description: v})} />
                  </div>
                </div>
              ) : modalType === 'month' ? (
                <>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Select Month</label>
                  <select 
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl outline-none focus:border-orange-500 font-bold text-sm"
                    value={formData.month_name}
                    onChange={(e) => setFormData({...formData, month_name: e.target.value})}
                  >
                    <option value="">Choose Month</option>
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <Input label="Custom Price (Optional)" value={formData.price} type="number" onChange={(v) => setFormData({...formData, price: v})} />
                </>
              ) : (
                <>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Material Category</label>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
                    {[
                      { id: 'video', label: 'Video', color: 'bg-red-500' },
                      { id: 'pdf', label: 'PDF', color: 'bg-blue-500' },
                      { id: 'live', label: 'Live Class', color: 'bg-teal-500' },
                      { id: 'recording', label: 'Recording', color: 'bg-purple-500' }
                    ].map(t => (
                      <button 
                        key={t.id}
                        type="button" 
                        onClick={() => setFormData({...formData, material_type: t.id})} 
                        className={`py-2 rounded-lg font-bold text-[10px] uppercase tracking-widest transition-all ${formData.material_type === t.id ? `${t.color} text-white shadow-lg` : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-4">
                    <Input label="Material Title" value={formData.title} placeholder="e.g. Lesson 01 - Theory" onChange={(v) => setFormData({...formData, title: v})} />
                    <Input label="Description (Optional)" value={formData.description} type="textarea" placeholder="Brief description of the material..." onChange={(v) => setFormData({...formData, description: v})} />
                    
                    {formData.material_type === 'pdf' ? (
                      <div className="space-y-1">
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Upload PDF File</label>
                        <input 
                          type="file" 
                          accept="application/pdf"
                          onChange={(e) => setFormData({...formData, pdf_file: e.target.files[0]})}
                          className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:border-orange-500 font-bold text-sm transition-all shadow-inner file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100" 
                        />
                        {formData.material_url && !formData.pdf_file && (
                          <p className="text-xs text-slate-500 mt-2 italic">Current file: <a href={formData.material_url} target="_blank" rel="noreferrer" className="text-blue-500 underline">View PDF</a></p>
                        )}
                      </div>
                    ) : (
                      <Input label="Resource URL" value={formData.material_url} placeholder="e.g. https://zoom.us/... or YouTube link" onChange={(v) => setFormData({...formData, material_url: v})} />
                    )}
                    {formData.material_type === 'live' && (
                      <div className="grid grid-cols-2 gap-4">
                        <Input label="Start Time" type="datetime-local" value={formData.scheduled_at} onChange={(v) => setFormData({...formData, scheduled_at: v})} />
                        <Input label="End Time" type="datetime-local" value={formData.ended_at} onChange={(v) => setFormData({...formData, ended_at: v})} />
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl uppercase tracking-widest text-xs">Cancel</button>
                <button type="submit" className="flex-1 py-4 bg-orange-500 text-white font-black rounded-2xl shadow-lg shadow-orange-100 hover:bg-orange-600 transition-all uppercase tracking-widest text-xs">Save Details</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Input = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div className="space-y-1">
    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{label}</label>
    {type === "textarea" ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:border-orange-500 font-bold text-sm h-32 transition-all shadow-inner" />
    ) : (
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:border-orange-500 font-bold text-sm transition-all shadow-inner" />
    )}
  </div>
);

export default ManageCourses;
